import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Message-rate page — polls /api/guilds/:id/message-rate every 15s.
 * Shows big-number for last full minute + rolling aggregates + inline SVG
 * sparkline of the last 60 minutes so trends are visible at a glance.
 */

interface MessageRate {
  currentMinute: number;
  currentPartial: number;
  perMinute: number;
  last5MinAvg: number;
  last15MinAvg: number;
  last60MinTotal: number;
  history: Array<{ minute: number; count: number }>;
}

const REFRESH_MS = 15_000;

export function MessageRate() {
  const { guildId } = useParams<{ guildId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['message-rate', guildId],
    queryFn: () => api<MessageRate>(`/api/guilds/${guildId}/message-rate`),
    enabled: !!guildId,
    refetchInterval: REFRESH_MS,
  });

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Nachrichten-Rate</h1>
        <span className="text-xs text-muted-foreground">
          Aktualisiert alle {REFRESH_MS / 1000}s
        </span>
      </div>

      {isLoading && <p className="text-muted-foreground">Lade…</p>}
      {error && (
        <p className="text-sm text-destructive">
          Fehler: {(error as Error).message}
        </p>
      )}

      {data && (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-semibold">{data.perMinute}</div>
                <div className="text-xs text-muted-foreground">
                  Nachrichten/Min (letzte volle Minute)
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-semibold">
                  {data.last5MinAvg.toFixed(1)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Ø letzte 5 Min
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-semibold">
                  {data.last15MinAvg.toFixed(1)}
                </div>
                <div className="text-xs text-muted-foreground">
                  Ø letzte 15 Min
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-semibold">
                  {data.last60MinTotal}
                </div>
                <div className="text-xs text-muted-foreground">
                  Gesamt letzte 60 Min
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                Letzte 60 Minuten
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Sparkline data={data.history} />
              <p className="mt-3 text-xs text-muted-foreground">
                Aktuelle Minute (noch nicht abgeschlossen):{' '}
                <span className="font-medium text-foreground">
                  {data.currentPartial}
                </span>{' '}
                Nachrichten bisher.
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function Sparkline({ data }: { data: Array<{ minute: number; count: number }> }) {
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Daten.</p>;
  }
  const width = 720;
  const height = 140;
  const padding = 12;
  const max = Math.max(1, ...data.map((d) => d.count));
  const stepX = (width - 2 * padding) / Math.max(1, data.length - 1);
  const y = (v: number) => height - padding - ((height - 2 * padding) * v) / max;
  const points = data.map((d, i) => `${padding + i * stepX},${y(d.count)}`);
  const path = `M ${points.join(' L ')}`;
  const areaPath = `${path} L ${padding + (data.length - 1) * stepX},${height - padding} L ${padding},${height - padding} Z`;
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-32 w-full min-w-[480px]"
        preserveAspectRatio="none"
      >
        <path d={areaPath} fill="hsl(var(--primary) / 0.15)" />
        <path
          d={path}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <text
          x={padding}
          y={padding + 8}
          className="fill-current text-[10px] text-muted-foreground"
        >
          Max: {max}
        </text>
      </svg>
    </div>
  );
}
